import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "./use-business";

export type FoodOrderRow = any;

export function useFoodOrders() {
  const { business } = useBusiness();
  const [orders, setOrders] = useState<FoodOrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrders = useCallback(async () => {
    if (!business?.id) {
      setOrders([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      // Prefer structured line items (and add-ons if available).
      // If the DB migration hasn't been applied yet, joins can cause a 400; fallback gracefully.
      const attemptWithAddons = await (supabase as any)
        .from("food_orders")
        .select(`*,items:food_order_items(*,addons:food_order_item_addons(*))`)
        .eq("business_id", business.id)
        .order("created_at", { ascending: false })
        .limit(500);

      if (!attemptWithAddons.error) {
        setOrders(attemptWithAddons.data || []);
        return;
      }

      // If add-ons relation doesn't exist yet, retry with items only.
      const msg = String((attemptWithAddons.error as any)?.message || "");
      const addonRelMissing =
        msg.toLowerCase().includes("food_order_item_addons") ||
        msg.toLowerCase().includes("relationship") ||
        msg.toLowerCase().includes("does not exist");

      if (addonRelMissing) {
        const attemptWithItemsOnly = await (supabase as any)
          .from("food_orders")
          .select(`*,items:food_order_items(*)`)
          .eq("business_id", business.id)
          .order("created_at", { ascending: false })
          .limit(500);
        if (!attemptWithItemsOnly.error) {
          // Normalize: ensure items have an addons array for UI stability
          const normalized = (attemptWithItemsOnly.data || []).map((o: any) => ({
            ...o,
            items: (o.items || []).map((it: any) => ({ ...it, addons: [] })),
          }));
          setOrders(normalized);
          return;
        }
      }

      const shouldFallback =
        msg.toLowerCase().includes("food_order_items") ||
        msg.toLowerCase().includes("does not exist") ||
        msg.toLowerCase().includes("relationship") ||
        Number((attemptWithAddons.error as any)?.status) === 400;

      if (!shouldFallback) throw attemptWithAddons.error;

      const plain = await (supabase as any)
        .from("food_orders")
        .select("*")
        .eq("business_id", business.id)
        .order("created_at", { ascending: false })
        .limit(500);

      if (plain.error) throw plain.error;
      // Normalize to keep UI stable
      const normalized = (plain.data || []).map((o: any) => ({ ...o, items: [] }));
      setOrders(normalized);
    } catch (e: any) {
      setError(e?.message || "Failed to fetch orders");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [business?.id]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const updateOrder = async (id: string, patch: Record<string, any>) => {
    const { error } = await (supabase as any).from("food_orders").update(patch).eq("id", id);
    if (error) throw error;
    await fetchOrders();
  };

  return {
    orders,
    loading,
    error,
    refetch: fetchOrders,
    updateOrder,
  };
}

